import { useAdOperatorAvailability } from '@backoffice-frontend/ad-operator-availability'
import { useErrorHandlingMutation } from '@backoffice-frontend/data-fetching'
import { useMachine } from '@xstate/react'
import {
  useMeetingManager,
  useRosterState,
} from 'amazon-chime-sdk-component-library-react'
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
} from 'react'
import {
  useFccInitiateOperatorToPassengerCallMutation,
  useFccInitiateOperatorToSafetyDriverCallMutation,
  useFccJoinPassengerCallMutation,
  useFccJoinSafetyDriverCallMutation,
} from '../CallControls/CallControls.hook'
import type { CallProviderVehicleFragment } from './CallProvider.hook'
import { type CallType, createCallMachine } from './callMachine'
import { useCallAlerts } from './useCallAlerts'

const useCallContextValue = ({
  vehicle,
}: {
  vehicle: CallProviderVehicleFragment
}) => {
  const { enableMicrophone } = useAdOperatorAvailability()
  const {
    hasCallAlert,
    hasIncomingPassengerCallAlert,
    hasIncomingSafetyDriverCallAlert,
    emergencyStopAlertId,
    callAlertCreatedAt,
  } = useCallAlerts(vehicle)

  const { roster } = useRosterState()
  const meetingManager = useMeetingManager()
  const meetingSession = meetingManager.meetingSession

  const [joinPassengerCallMutation] = useErrorHandlingMutation(
    useFccJoinPassengerCallMutation,
  )
  const [joinSafetyDriverCallMutation] = useErrorHandlingMutation(
    useFccJoinSafetyDriverCallMutation,
  )
  const [initiateOperatorToPassengerCallMutation] = useErrorHandlingMutation(
    useFccInitiateOperatorToPassengerCallMutation,
  )
  const [initiateOperatorToSafetyDriverCallMutation] = useErrorHandlingMutation(
    useFccInitiateOperatorToSafetyDriverCallMutation,
  )

  const machine = useMemo(() => {
    return createCallMachine({
      meetingManager,
      meetingSession,
      enableMicrophone,
      vehicleId: vehicle.id,
      emergencyStopAlertId,
      mutations: {
        joinPassengerCallMutation,
        joinSafetyDriverCallMutation,
        initiateOperatorToPassengerCallMutation,
        initiateOperatorToSafetyDriverCallMutation,
      },
    })
  }, [
    meetingManager,
    meetingSession,
    enableMicrophone,
    vehicle.id,
    emergencyStopAlertId,
    joinPassengerCallMutation,
    joinSafetyDriverCallMutation,
    initiateOperatorToPassengerCallMutation,
    initiateOperatorToSafetyDriverCallMutation,
  ])

  const [state, send] = useMachine(machine)

  // Advance state machine based on call alert updates
  useEffect(() => {
    switch (state.value) {
      case 'idle':
        if (hasIncomingPassengerCallAlert) {
          send({ type: 'INCOMING_CALL_RECEIVED', callType: 'passenger' })
        } else if (hasIncomingSafetyDriverCallAlert) {
          send({ type: 'INCOMING_CALL_RECEIVED', callType: 'safetyDriver' })
        }
        break
      case 'waitingForAlertCreation':
        if (hasCallAlert) {
          send({ type: 'ALERT_CREATED' })
        }
        break
      case 'ringing':
      case 'waitingForAlertResolution':
        if (!hasCallAlert) {
          send({ type: 'ALERT_RESOLVED' })
        }
        break
      default:
        break
    }
  }, [
    hasIncomingPassengerCallAlert,
    hasIncomingSafetyDriverCallAlert,
    hasCallAlert,
    send,
    state,
  ])

  // Operator joins the meeting
  useEffect(() => {
    const attendeeId = meetingSession?.configuration.credentials?.attendeeId
    if (!attendeeId) {
      return
    }
    meetingSession.audioVideo.addObserver({
      audioVideoDidStart: () => {
        if (state.value === 'joiningIncomingCall') {
          send({ type: 'OPERATOR_JOINED' })
        }
      },
      audioVideoDidStop: () => {
        send({ type: 'CHIME_MEETING_ENDED' })
      },
    })
  }, [meetingSession, send, state])

  // Callee joins the meeting
  useEffect(() => {
    const attendeeId = meetingSession?.configuration.credentials?.attendeeId
    if (!attendeeId || state.value !== 'waitingForCalleeToJoin') {
      return
    }

    const otherAttendees = Object.keys(roster).filter(id => id !== attendeeId)
    if (otherAttendees.length > 0) {
      send({ type: 'CALLEE_JOINED' })
    }
  }, [roster, meetingSession, state, send])

  return {
    pickup: () => send({ type: 'OPERATOR_PICKED_UP' }),
    hangup: () => send({ type: 'OPERATOR_HUNG_UP' }),
    toggleMute: () => send({ type: 'MUTE_TOGGLED' }),
    initiateOutgoingCall: (callType: CallType) =>
      send({ type: 'OUTGOING_CALL_STARTED', callType }),
    state,
    callAlertCreatedAt,
    hasCallAlert,
  }
}

type CallContextValue = ReturnType<typeof useCallContextValue>

const CallContext = createContext<CallContextValue | null>(null)

export function CallProvider({
  vehicle,
  children,
}: {
  vehicle: CallProviderVehicleFragment
  children: ReactNode
}) {
  const callContextValue = useCallContextValue({
    vehicle,
  })
  return (
    <CallContext.Provider value={callContextValue}>
      {children}
    </CallContext.Provider>
  )
}

export const useCall = () => {
  const callContextValue = useContext(CallContext)

  if (callContextValue === null) {
    throw new Error('useCall must be used within a CallProvider')
  }

  return callContextValue
}
